import { type FormEvent, useState } from 'react';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiError, api } from '../api/client';
import { signIn, signOut } from '../auth/supabase';
import { EyeIcon, EyeOffIcon, LockIcon, MailIcon, SignInIcon } from '../components/icons';

interface Props {
  onSignedIn: () => void;
}

/**
 * Sign-in has two independent failure points, and they need different messages:
 *   1. Supabase verifies the password   -> wrong credentials
 *   2. our API verifies the CRM profile -> right password, no access
 * Collapsing them would tell a deactivated operator to check their password,
 * which they would then do forever.
 */
export default function Login({ onSignedIn }: Props) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string }>({});
  const [busy, setBusy] = useState(false);

  function validate(): boolean {
    const next: { email?: string; password?: string } = {};
    if (!email.trim()) next.email = 'Enter your email address.';
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) next.email = 'That does not look like an email address.';
    if (!password) next.password = 'Enter your password.';
    setFieldErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!validate()) return;

    setBusy(true);
    try {
      const result = await signIn(email.trim().toLowerCase(), password);
      if (!result.ok) {
        setError(result.message);
        return;
      }

      // Authenticated with Supabase — but is this account allowed in the CRM?
      try {
        await api.me();
      } catch (err) {
        // Leaving the Supabase session in place would strand the user: signed
        // in as far as the browser knows, refused by every request.
        await signOut();
        setError(
          err instanceof ApiError && (err.code === 'no_crm_profile' || err.code === 'account_disabled')
            ? err.message
            : `Signed in, but the CRM could not be reached${err instanceof ApiError ? `: ${err.message}` : '.'}`,
        );
        return;
      }

      onSignedIn();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-ground grid min-h-full place-items-center px-5 py-12">
      <header className="mb-7 flex flex-col items-center gap-2.5 text-center">
        <img src="/logo.png" alt="ePettyshop" className="mb-1.5 h-auto w-[min(300px,70vw)]" />
        <h1 className="text-[clamp(26px,5vw,32px)] font-extrabold tracking-tight">ePettyshop CRM</h1>
        <p className="text-[17px] text-muted">Sign in to continue</p>
      </header>

      <div className="w-full max-w-[430px] rounded-[var(--radius-card)] bg-surface p-8 shadow-[0_10px_30px_rgba(35,45,90,0.08)]">
        <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-5">
          {error && <Alert>{error}</Alert>}

          <div className="flex flex-col gap-2">
            <Label htmlFor="email">Email Address</Label>
            <Input
              leading={<MailIcon />}
              id="email"
              name="email"
              type="email"
              inputMode="email"
              autoComplete="username"
              placeholder="Enter your email"
              value={email}
              disabled={busy}
              aria-invalid={Boolean(fieldErrors.email)}
              aria-describedby={fieldErrors.email ? 'email-error' : undefined}
              onChange={(e) => setEmail(e.target.value)}
            />
            {fieldErrors.email && (
              <span className="text-[13px] text-danger" id="email-error">
                {fieldErrors.email}
              </span>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="password">Password</Label>
            <Input
              leading={<LockIcon />}
              trailing={
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                  disabled={busy}
                >
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </Button>
              }
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="Enter your password"
              value={password}
              disabled={busy}
              aria-invalid={Boolean(fieldErrors.password)}
              aria-describedby={fieldErrors.password ? 'password-error' : undefined}
              onChange={(e) => setPassword(e.target.value)}
            />
            {fieldErrors.password && (
              <span className="text-[13px] text-danger" id="password-error">
                {fieldErrors.password}
              </span>
            )}
          </div>

          <Button type="submit" size="lg" loading={busy} className="mt-0.5 w-full">
            {!busy && <SignInIcon />}
            {busy ? 'Signing in…' : 'Sign In'}
          </Button>
        </form>
      </div>
    </div>
  );
}
